import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  Building2, Loader2, ArrowLeft, Save, Copy, Check, RefreshCw, Link2, UserCog, Power, Bot, ExternalLink,
  BarChart3, Ban,
} from "lucide-react";
import { toast } from "sonner";

import TopNav from "@/components/TopNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  adminGetCompany, adminUpdateCompany, adminActivateCompany, adminDeactivateCompany, adminCompanyAudit,
  adminCompanyNotifications, adminGetInterviewLink, adminRotateInterviewLink, adminRevokeInterviewLink,
  adminSetCompanyMaster, adminUnsetCompanyMaster, adminUnassignedAgents, adminAssignAgentCompany,
  adminAssignSessionCompany, adminListCompanies, adminListUsers, listCompanyAgents, apiError,
} from "@/lib/api";
import {
  ActiveBadge, AgentStateBadge, DemoBadge, TokenAlertBadge, TokenAlertsBanner, Field, errDetail, fmtDate,
} from "@/components/company/CompanyUi";
import CompanyLlmKeysPanel from "@/components/company/CompanyLlmKeysPanel";
import DepartmentsPanel, { SeatsMeter } from "@/components/company/DepartmentsPanel";

const TABS = [
  { id: "datos", label: "Datos" },
  { id: "master", label: "Usuario master" },
  { id: "plan", label: "Plan y seats" },
  { id: "departamentos", label: "Departamentos y accesos" },
  { id: "enlace", label: "Enlace de entrevista" },
  { id: "claves", label: "Claves LLM" },
  { id: "agentes", label: "Agentes" },
];

const AUDIT_LABEL = {
  company_deactivate: "Empresa desactivada",
  company_activate: "Empresa reactivada",
  agent_assign_company: "Agente asignado",
};

export default function AdminCompanyDetail() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.id === params.get("pestana")) ? params.get("pestana") : "datos";
  const setTab = (id) => setParams({ pestana: id }, { replace: true });

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [confirmState, setConfirmState] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { company: c } = await adminGetCompany(slug);
      setCompany(c);
    } catch (e) {
      if (e?.response?.status === 404) setNotFound(true);
      else toast.error(errDetail(e, "No se pudo cargar la ficha."));
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleUi = async (v) => {
    try {
      const { company: c } = await adminUpdateCompany(slug, { llm_ui_visible: v });
      setCompany(c);
      toast.success(v ? "El cliente ya puede ver su pantalla de claves." : "Pantalla de claves oculta para el cliente.");
    } catch (e) {
      toast.error(errDetail(e, "No se pudo cambiar la visibilidad."));
    }
  };

  const changeState = async () => {
    setBusy(true);
    try {
      const { company: c } = company.active
        ? await adminDeactivateCompany(slug, reason.trim())
        : await adminActivateCompany(slug);
      setCompany(c);
      toast.success(c.active ? "Empresa reactivada: sus agentes vuelven a responder." : "Empresa desactivada.");
      setReason("");
    } catch (e) {
      toast.error(errDetail(e, "No se pudo cambiar el estado."));
    } finally {
      setBusy(false);
      setConfirmState(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white"><TopNav />
        <div className="flex items-center justify-center py-32 text-slate-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Cargando…</div>
      </div>
    );
  }
  if (notFound || !company) {
    return (
      <div className="min-h-screen bg-white"><TopNav />
        <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="company-not-found">
          <p className="font-display text-3xl text-ink">Cliente no encontrado</p>
          <Link to="/admin/clientes" className="mt-6 inline-block text-sm text-accent-blue underline" data-testid="back-to-companies">Volver a la lista</Link>
        </div>
      </div>
    );
  }

  const isDemo = !!company.is_demo;

  return (
    <div className="min-h-screen bg-white text-ink" data-testid="admin-company-detail">
      <TopNav />
      <div className="mx-auto max-w-screen-xl px-6 py-10 sm:py-12">
        <Link to="/admin/clientes" className="inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] text-slate-500 hover:text-ink"
              data-testid="back-to-companies">
          <ArrowLeft className="h-3 w-3" /> Clientes
        </Link>
        <div className="mt-4 flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <p className="eyebrow flex items-center gap-1.5"><Building2 className="h-3 w-3" /> Ficha de cliente</p>
            <h1 className="mt-2 break-words font-display text-4xl text-ink" data-testid="company-name">{company.name}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="font-mono text-[12px] text-slate-500">{company.slug}</span>
              <ActiveBadge active={company.active} testid="company-state-badge" />
              {isDemo && <DemoBadge />}
              <TokenAlertBadge level={company.token_alert_level || "none"} testid="company-alert-badge" />
              <span className="font-mono text-[11px] text-slate-500">
                Agentes {company.agents_active}/{company.agents_total} · {company.sessions} sesiones · {company.users} usuarios
              </span>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <label className="flex items-center gap-3 text-sm text-slate-700">
              <Switch checked={!!company.llm_ui_visible} onCheckedChange={toggleUi} data-testid="toggle-llm-ui-visible" />
              Mostrar la sección «Claves de IA» al master del cliente
              <span className="sr-only"> (si está apagado, el master ve «Su administrador no ha habilitado esta sección»)</span>
            </label>
            <div className="text-right">
              <Button
                onClick={() => setConfirmState(true)}
                disabled={isDemo && company.active}
                variant="outline"
                className={`rounded-sm text-[11px] uppercase tracking-[0.16em] ${company.active ? "border-red-300 text-red-700 hover:bg-red-50" : "border-emerald-400 text-emerald-800 hover:bg-emerald-50"}`}
                data-testid={company.active ? "deactivate-company-btn" : "activate-company-btn"}
              >
                <Power className="mr-1.5 h-3.5 w-3.5" /> {company.active ? "Desactivar empresa" : "Reactivar empresa"}
              </Button>
              {isDemo && (
                <p className="mt-1 max-w-xs text-[11px] text-slate-500" data-testid="demo-cannot-deactivate">
                  Las empresas demo (SimpleTV, FinFarma y otras) no se pueden desactivar: las usa el tutorial.
                </p>
              )}
            </div>
          </div>
        </div>

        {!company.active && (
          <div className="mt-6 border border-red-300 bg-red-50 p-4 text-sm text-red-900" data-testid="company-inactive-banner">
            <p className="font-semibold">Empresa desactivada{company.deactivated_at ? ` (${fmtDate(company.deactivated_at)})` : ""}</p>
            <p className="mt-1">
              Sus agentes no responden y su enlace de entrevista no admite sesiones nuevas. No se borró nada: al
              reactivarla todo vuelve a funcionar.{company.deactivation_reason ? ` Motivo: ${company.deactivation_reason}.` : ""}
            </p>
          </div>
        )}

        <TokenAlertsBanner level={company.token_alert_level} alerts={company.token_alerts} onGoKeys={() => setTab("claves")} />

        <div className="mt-8 flex flex-wrap items-center gap-1 border-b border-slate-200">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`company-tab-${t.id}`}
                    className={`relative px-4 py-2 text-[12px] font-medium transition ${tab === t.id ? "text-ink" : "text-slate-500 hover:text-ink"}`}>
              {t.label}
              {tab === t.id && <span className="pointer-events-none absolute inset-x-2 -bottom-px h-[2px] bg-gradient-to-r from-accent-blue to-accent-purple" />}
            </button>
          ))}
        </div>

        <div className="mt-8">
          {tab === "datos" && <DataTab company={company} onSaved={setCompany} />}
          {tab === "master" && <MasterTab company={company} onSaved={setCompany} />}
          {tab === "plan" && <PlanTab company={company} onSaved={setCompany} onGoDepartments={() => setTab("departamentos")} />}
          {tab === "departamentos" && <DepartmentsPanel slug={company.slug} />}
          {tab === "enlace" && <LinkTab company={company} />}
          {tab === "claves" && <KeysTab company={company} onChanged={load} />}
          {tab === "agentes" && <AgentsTab company={company} onChanged={load} />}
        </div>
      </div>

      <AlertDialog open={confirmState} onOpenChange={(v) => !busy && setConfirmState(v)}>
        <AlertDialogContent data-testid="company-state-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{company.active ? `Desactivar ${company.name}` : `Reactivar ${company.name}`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-slate-600">
                {company.active ? (
                  <>
                    <p>Todos los agentes de la empresa quedarán <strong>inactivos</strong>: dejarán de responder a sus usuarios y el enlace de entrevista no admitirá sesiones nuevas.</p>
                    <p>No se borra nada: agentes, claves, consumo, usuarios e informes se conservan, y al reactivar la empresa todo vuelve a funcionar al instante.</p>
                  </>
                ) : (
                  <p>Los agentes de la empresa volverán a responder y el enlace de entrevista volverá a funcionar.</p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {company.active && (
            <Field label="Motivo (opcional, queda en el historial)">
              <Input value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} className="rounded-sm" data-testid="deactivate-reason" />
            </Field>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="company-state-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); changeState(); }} disabled={busy}
                               className={company.active ? "bg-red-600 hover:bg-red-700" : ""} data-testid="company-state-confirm">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {company.active ? "Desactivar" : "Reactivar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SaveBtn({ onClick, saving, testid, children = "Guardar" }) {
  return (
    <Button onClick={onClick} disabled={saving} data-testid={testid}
            className="rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white hover:bg-slate-800">
      {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />} {children}
    </Button>
  );
}

// Fix 9: same list as companies.CURRENCIES on the server (ISO 4217)
const CURRENCIES = ["USD", "EUR", "VES", "COP", "MXN", "ARS", "CLP", "PEN", "BRL", "DOP", "CRC", "GTQ", "UYU",
  "ANG", "AWG", "BOB", "PYG", "HNL", "NIO", "PAB", "CAD", "GBP"];

function DataTab({ company, onSaved }) {
  const [form, setForm] = useState({
    name: company.name || "", contact_email: company.contact_email || "", contact_phone: company.contact_phone || "",
    country: company.country || "", notes: company.notes || "", currency: company.currency || "USD",
  });
  const [saving, setSaving] = useState(false);
  const [audit, setAudit] = useState([]);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    adminCompanyAudit(company.slug).then((d) => setAudit(d.audit || [])).catch(() => setAudit([]));
  }, [company.slug, company.active]);

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("El nombre es obligatorio.");
      return;
    }
    setSaving(true);
    try {
      const { company: c } = await adminUpdateCompany(company.slug, form);
      onSaved(c);
      toast.success("Datos guardados.");
    } catch (e) {
      toast.error(errDetail(e, "No se pudieron guardar los datos."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]" data-testid="tab-datos">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nombre" required>
          <Input value={form.name} onChange={(e) => upd("name", e.target.value)} className="rounded-sm" data-testid="cd-name" />
        </Field>
        <Field label="País">
          <Input value={form.country} onChange={(e) => upd("country", e.target.value)} className="rounded-sm" data-testid="cd-country" />
        </Field>
        <Field label="Moneda" hint="Se usa en la consultoría (costo por hora) y en el ahorro estimado de los agentes que se generen a partir de ahora. Los agentes existentes conservan la suya.">
          <select value={form.currency} onChange={(e) => upd("currency", e.target.value)}
            className="h-10 w-full rounded-sm border border-slate-300 bg-white px-3 text-sm text-slate-900" data-testid="cd-currency">
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label="Correo de contacto" hint="Recibe los avisos de tokens si no hay usuario master.">
          <Input type="email" value={form.contact_email} onChange={(e) => upd("contact_email", e.target.value)} className="rounded-sm" data-testid="cd-email" />
        </Field>
        <Field label="Teléfono">
          <Input value={form.contact_phone} onChange={(e) => upd("contact_phone", e.target.value)} className="rounded-sm" data-testid="cd-phone" />
        </Field>
        <div className="sm:col-span-2">
          <Field label="Notas">
            <Textarea rows={4} value={form.notes} onChange={(e) => upd("notes", e.target.value)} className="rounded-sm" data-testid="cd-notes" />
          </Field>
        </div>
        <div><SaveBtn onClick={save} saving={saving} testid="cd-save" /></div>
      </div>
      <aside className="space-y-4 text-[12px] text-slate-600">
        <div className="border border-slate-200 p-4">
          <p className="eyebrow">Registro</p>
          <p className="mt-2">Creada: {fmtDate(company.created_at)}</p>
          <p>Actualizada: {fmtDate(company.updated_at)}</p>
          {company.deactivated_at && <p>Última desactivación: {fmtDate(company.deactivated_at)}</p>}
          {company.reactivated_at && <p>Última reactivación: {fmtDate(company.reactivated_at)}</p>}
        </div>
        <div className="border border-slate-200 p-4" data-testid="company-audit">
          <p className="eyebrow">Historial de estado</p>
          {audit.length === 0 ? <p className="mt-2 text-slate-400">Sin cambios registrados.</p> : (
            <ul className="mt-2 space-y-1.5">
              {audit.slice(0, 10).map((a) => (
                <li key={a.id}>
                  <span className="font-medium text-slate-800">{AUDIT_LABEL[a.action] || a.action}</span> · {fmtDate(a.at)}
                  {a.actor_email ? ` · ${a.actor_email}` : ""}{a.details?.reason ? ` · ${a.details.reason}` : ""}
                </li>
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function MasterTab({ company, onSaved }) {
  const [mode, setMode] = useState("existing");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [confirmUnset, setConfirmUnset] = useState(false);
  const m = company.master;

  useEffect(() => {
    adminListUsers({ role: "cliente" })
      .then((d) => setClients((d.users || []).filter((u) => u.role === "cliente")))
      .catch(() => setClients([]));
  }, []);

  const submit = async () => {
    const em = email.trim().toLowerCase();
    if (!em) {
      toast.error("Indique el correo del usuario.");
      return;
    }
    if (mode === "new" && (password.length < 8 || password.length > 128)) {
      toast.error("La contraseña inicial debe tener entre 8 y 128 caracteres.");
      return;
    }
    const body = mode === "new" ? { email: em, password, name: name.trim() } : { email: em };
    setSaving(true);
    try {
      const r = await adminSetCompanyMaster(company.slug, body);
      onSaved(r.company);
      toast.success(r.created ? "Usuario master creado y designado." : "Usuario master designado.");
      setEmail("");
      setName("");
    } catch (e) {
      toast.error(errDetail(e, "No se pudo designar el usuario master."));
    } finally {
      setPassword(""); // never kept after the request
      body.password = undefined;
      setSaving(false);
    }
  };

  const unset = async () => {
    try {
      const { company: c } = await adminUnsetCompanyMaster(company.slug);
      onSaved(c);
      toast.success("Designación quitada. El usuario sigue en la empresa como cliente.");
    } catch (e) {
      toast.error(errDetail(e, "No se pudo quitar la designación."));
    }
  };

  return (
    <div className="grid grid-cols-1 gap-10 lg:grid-cols-2" data-testid="tab-master">
      <div>
        <p className="eyebrow flex items-center gap-1.5"><UserCog className="h-3 w-3" /> Usuario master actual</p>
        {m ? (
          <div className="mt-3 border border-slate-200 p-5" data-testid="current-master">
            <p className="font-display text-lg text-ink">{m.name || m.email}</p>
            <p className="font-mono text-[12px] text-accent-blue">{m.email}</p>
            <p className="mt-1 text-[11px] uppercase tracking-[0.12em] text-slate-500">Estado: {m.status}</p>
            <Button variant="outline" size="sm" onClick={() => setConfirmUnset(true)}
                    className="mt-4 h-8 rounded-sm border-red-300 text-[11px] uppercase tracking-[0.14em] text-red-700 hover:bg-red-50"
                    data-testid="unset-master-btn">
              Quitar designación
            </Button>
          </div>
        ) : (
          <p className="mt-3 border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-500" data-testid="no-master">
            Sin usuario master. Los avisos de tokens se envían al correo de contacto de la ficha.
          </p>
        )}
        <p className="mt-4 text-[12px] leading-relaxed text-slate-500">
          El usuario master es un usuario con rol cliente de esta empresa. Ve el panel de su empresa, edita sus agentes,
          recibe los avisos de consumo y, si usted lo habilita, administra las claves de IA.
        </p>
      </div>
      <div>
        <p className="eyebrow">{m ? "Sustituir usuario master" : "Designar usuario master"}</p>
        <div className="mt-3 flex gap-2">
          {[["existing", "Usuario existente"], ["new", "Crear usuario nuevo"]].map(([id, label]) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`master-mode-${id}`}
                    className={`rounded-sm border px-3 py-1.5 text-[12px] ${mode === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="mt-4 space-y-4">
          <Field label="Correo" required hint={mode === "existing" ? "Debe ser un usuario con rol cliente, sin empresa o de esta empresa." : "Se crea aprobado, con rol cliente y vinculado a esta empresa."}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} list="client-users"
                   autoComplete="off" className="rounded-sm" data-testid="master-email" />
            {mode === "existing" && (
              <datalist id="client-users">
                {clients.map((u) => <option key={u.id} value={u.email}>{u.name}</option>)}
              </datalist>
            )}
          </Field>
          {mode === "new" && (
            <>
              <Field label="Nombre">
                <Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="master-name" />
              </Field>
              <Field label="Contraseña inicial" required hint="De 8 a 128 caracteres. Solo se envía al servidor; no se guarda ni se vuelve a mostrar. Comuníquela al cliente por un canal seguro.">
                <Input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="rounded-sm" data-testid="master-password" />
              </Field>
            </>
          )}
          <SaveBtn onClick={submit} saving={saving} testid="master-submit">{mode === "new" ? "Crear y designar" : "Designar"}</SaveBtn>
        </div>
      </div>

      <AlertDialog open={confirmUnset} onOpenChange={setConfirmUnset}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Quitar la designación de usuario master</AlertDialogTitle>
            <AlertDialogDescription>
              El usuario seguirá en la empresa como cliente normal, pero dejará de recibir los avisos de consumo y de gestionar agentes y claves.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="unset-master-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={unset} data-testid="unset-master-confirm">Quitar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PlanTab({ company, onSaved, onGoDepartments }) {
  const [seats, setSeats] = useState(String(company.seats_plan ?? 0));
  const [saving, setSaving] = useState(false);
  const [inUse, setInUse] = useState(null); // 409 seats_in_use (step 5b)
  const n = Number(seats);
  const unchanged = n === Number(company.seats_plan ?? 0); // a legacy value (e.g. 7) re-sent as is is accepted
  const invalid = seats === "" || !Number.isInteger(n) || n < 0 || n > 100000 || (n % 5 !== 0 && !unchanged);
  const used = company.seats?.used ?? 0;
  const belowUse = !invalid && n < used;

  const save = async () => {
    setInUse(null);
    if (invalid) {
      toast.error("Los seats del plan deben ser 0 o un múltiplo de 5 (5, 10, 15…).");
      return;
    }
    setSaving(true);
    try {
      const { company: c } = await adminUpdateCompany(company.slug, { seats_plan: n });
      onSaved(c);
      toast.success("Plan actualizado.");
    } catch (e) {
      const err = apiError(e, "No se pudo guardar el plan.");
      if (err.code === "seats_in_use") setInUse(err.detail);
      else toast.error(errDetail(e, "No se pudo guardar el plan."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_380px]" data-testid="tab-plan">
      <div className="max-w-xl space-y-4">
        <Field label="Seats del plan (personas con acceso)" required
               hint={invalid ? "Debe ser 0 o un múltiplo de 5 (5, 10, 15…)." : `Plan actual: ${company.seats_plan || 0} seats · en uso hoy: ${used}.`}>
          <Input type="number" min={0} step={5} value={seats} onChange={(e) => { setSeats(e.target.value); setInUse(null); }}
                 className={`w-40 rounded-sm font-mono ${invalid || belowUse ? "border-red-400" : ""}`} data-testid="seats-input" />
        </Field>
        {invalid && seats !== "" && (
          <p className="text-[12px] text-red-700" data-testid="seats-invalid">
            El plan se vende en bloques de 5 seats: indique 0, 5, 10, 15…
          </p>
        )}
        {belowUse && !inUse && (
          <p className="text-[12px] text-amber-800" data-testid="seats-below-use-hint">
            Hay {used} seats en uso: el servidor no permitirá reducir el plan por debajo de esa cifra.
          </p>
        )}
        {inUse && (
          <div className="border border-red-300 bg-red-50 p-3 text-[13px] text-red-900" role="alert" data-testid="seats-in-use-error">
            <p>{inUse}</p>
            <button onClick={onGoDepartments} className="mt-1 text-[12px] font-medium underline underline-offset-4" data-testid="seats-go-departments">
              Ir a Departamentos y accesos
            </button>
          </div>
        )}
        <p className="border border-slate-200 bg-slate-50 p-3 text-[12px] text-slate-600" data-testid="seats-note">
          El plan se vende en bloques de 5 seats. Ocupa un seat cada persona distinta: los usuarios de la empresa con
          login (salvo el master) y los colaboradores agregados a algún departamento. Se puede subir el plan cuando se
          quiera; para bajarlo, primero hay que liberar seats.
        </p>
        <SaveBtn onClick={save} saving={saving} testid="seats-save" />
      </div>
      <div className="self-start"><SeatsMeter seats={company.seats} testid="plan-seats-meter" /></div>
    </div>
  );
}

function LinkTab({ company }) {
  const [link, setLink] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [confirm, setConfirm] = useState(null); // "rotate" | "revoke"

  useEffect(() => {
    adminGetInterviewLink(company.slug).then(setLink).catch((e) => toast.error(errDetail(e, "No se pudo cargar el enlace.")))
      .finally(() => setLoading(false));
  }, [company.slug]);

  const url = link?.path ? `${window.location.origin}${link.path}` : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("No se pudo copiar. Seleccione el enlace y cópielo a mano.");
    }
  };
  const act = async (kind) => {
    const hadLink = !!link?.token;
    try {
      const d = kind === "revoke" ? await adminRevokeInterviewLink(company.slug) : await adminRotateInterviewLink(company.slug);
      setLink(d);
      toast.success(kind === "revoke" ? "Enlace revocado." : hadLink ? "Enlace regenerado. El anterior dejó de funcionar." : "Enlace generado.");
    } catch (e) {
      toast.error(errDetail(e, "No se pudo actualizar el enlace."));
    } finally {
      setConfirm(null);
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;

  return (
    <div className="max-w-3xl space-y-4" data-testid="tab-enlace">
      <p className="text-sm text-slate-600">
        Comparta este enlace con la empresa para que haga la entrevista ABC. Las sesiones creadas con él quedan
        asociadas a <strong>{company.name}</strong>: su informe se indexa en la biblioteca de la empresa y los agentes
        que se generen le pertenecen.
      </p>
      {!company.active && (
        <p className="border border-red-200 bg-red-50 p-3 text-[12px] text-red-800" data-testid="link-inactive-note">
          La empresa está desactivada: el enlace no admite sesiones nuevas hasta reactivarla.
        </p>
      )}
      {url ? (
        <>
          <div className="flex items-center gap-2 border border-slate-200 bg-slate-50 px-3 py-2">
            <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
            <code className="flex-1 truncate font-mono text-[12px] text-slate-800" data-testid="interview-link-url">{url}</code>
            <button onClick={copy} className="text-slate-500 hover:text-slate-900" title="Copiar enlace" data-testid="copy-interview-link">
              {copied ? <Check className="h-4 w-4 text-accent-blue" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <p className="font-mono text-[11px] text-slate-500">Generado: {fmtDate(link.created_at)}</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setConfirm("rotate")} className="rounded-sm text-[11px] uppercase tracking-[0.14em]"
                    data-testid="rotate-interview-link">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Regenerar enlace
            </Button>
            <Button variant="outline" onClick={() => setConfirm("revoke")}
                    className="rounded-sm border-red-300 text-[11px] uppercase tracking-[0.14em] text-red-700 hover:bg-red-50"
                    data-testid="revoke-interview-link">
              <Ban className="mr-1.5 h-3.5 w-3.5" /> Revocar
            </Button>
          </div>
        </>
      ) : (
        <div className="border border-dashed border-slate-300 bg-slate-50 p-6" data-testid="no-interview-link">
          <p className="text-sm text-slate-600">Esta empresa todavía no tiene enlace de entrevista.</p>
          <Button onClick={() => act("rotate")} className="mt-4 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.16em] text-white"
                  data-testid="create-interview-link">
            <Link2 className="mr-1.5 h-3.5 w-3.5" /> Generar enlace
          </Button>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm === "revoke" ? "Revocar el enlace de entrevista" : "Regenerar el enlace de entrevista"}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "revoke"
                ? "El enlace actual dejará de funcionar y la empresa no podrá iniciar entrevistas nuevas hasta que genere otro."
                : "Se crea un enlace nuevo y el actual deja de funcionar de inmediato. Las entrevistas ya iniciadas no cambian. Deberá enviar el enlace nuevo a la empresa."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="link-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => act(confirm)} data-testid="link-confirm-ok">
              {confirm === "revoke" ? "Revocar" : "Regenerar"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const NOTIF_STATUS = {
  queued: "En cola", sent: "Enviado", failed: "Falló", pending_no_smtp: "Sin SMTP configurado",
  no_recipient: "Sin destinatario", superseded_by_100: "Reemplazado por el aviso del 100 %",
};

function KeysTab({ company, onChanged }) {
  const [notifs, setNotifs] = useState([]);
  const loadNotifs = useCallback(() => {
    adminCompanyNotifications(company.slug, 10).then((d) => setNotifs(d.notifications || [])).catch(() => setNotifs([]));
  }, [company.slug]);
  useEffect(() => {
    loadNotifs();
  }, [loadNotifs]);

  return (
    <div className="space-y-10" data-testid="tab-claves">
      {company.is_demo && (
        <p className="border border-amber-300 bg-amber-50 p-3 text-[12px] text-amber-900">
          Empresa demo: sus agentes usan la clave de Inviktus sin límites. Las claves de esta pantalla no se usan mientras sea demo.
        </p>
      )}
      <CompanyLlmKeysPanel slug={company.slug} agents={company.agents || []} onChanged={() => { onChanged(); loadNotifs(); }} />
      <div data-testid="company-notifications">
        <p className="eyebrow">Avisos de consumo enviados (últimos 10)</p>
        <p className="mt-1 text-[12px] text-slate-500">Solo al usuario master (o al correo de contacto). Nunca a Inviktus.</p>
        {notifs.length === 0 ? <p className="mt-3 text-sm text-slate-400">Sin avisos.</p> : (
          <ul className="mt-3 divide-y divide-slate-200 border border-slate-200 text-[12px]">
            {notifs.map((n) => (
              <li key={n.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2">
                <span className="font-mono">{fmtDate(n.created_at)}</span>
                <span className="font-medium">{n.kind === "llm_alert_100" ? "Aviso 100 %" : "Aviso 80 %"}</span>
                <span>{n.agent_slug ? `Agente ${n.agent_slug}` : "Clave genérica"}</span>
                <span>{NOTIF_STATUS[n.status] || n.status}</span>
                <span className="font-mono text-slate-500">{(n.to || []).join(", ") || "—"}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AgentsTab({ company, onChanged }) {
  const [agents, setAgents] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [target, setTarget] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [a, u, c] = await Promise.all([
        listCompanyAgents(company.slug),
        adminUnassignedAgents(),
        adminListCompanies(),
      ]);
      setAgents(a.agents || []);
      setUnassigned(u.agents || []);
      setCompanies((c.companies || []).filter((x) => !x.is_demo));
    } catch (e) {
      toast.error(errDetail(e, "No se pudieron cargar los agentes."));
    } finally {
      setLoading(false);
    }
  }, [company.slug]);

  useEffect(() => {
    load();
  }, [load]);

  const assign = async (a) => {
    const dest = target[a.slug] || company.slug;
    // Fix 7 (B6): the assignment cannot be undone (the server answers 409 to a second one)
    const what = a.session_exists && (a.assign_via || "").includes("/sessions/")
      ? "la sesión y todos sus agentes" : `el agente «${a.name || a.slug}»`;
    if (!window.confirm(`¿Asignar ${what} a la empresa «${dest}»?\n\nLa asignación es definitiva: no se puede deshacer ni cambiar de empresa después.`)) return;
    try {
      if (a.session_exists && (a.assign_via || "").includes("/sessions/")) {
        await adminAssignSessionCompany(a.session_id, dest);
        toast.success("Sesión asignada: todos sus agentes pasan a la empresa y el informe se indexa en su biblioteca.");
      } else {
        await adminAssignAgentCompany(a.slug, dest);
        toast.success("Agente asignado a la empresa.");
      }
      await load();
      onChanged();
    } catch (e) {
      toast.error(errDetail(e, "No se pudo asignar el agente."));
    }
  };

  if (loading) return <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;

  return (
    <div className="space-y-10" data-testid="tab-agentes">
      <div>
        <p className="eyebrow">Agentes de {company.name} ({agents.length})</p>
        {agents.length === 0 ? (
          <p className="mt-3 text-sm text-slate-500" data-testid="company-agents-empty">
            Sin agentes. Se crean al generar agentes desde el informe de una entrevista hecha con el enlace de la empresa.
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-200 border border-slate-200">
            {agents.map((a) => (
              <li key={a.slug} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`company-agent-row-${a.slug}`}>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Bot className="h-4 w-4 text-slate-400" />
                    <span className="font-display text-base text-ink">{a.name}</span>
                    <AgentStateBadge active={a.active} testid={`agent-state-${a.slug}`} />
                  </div>
                  <p className="text-[12px] text-slate-500">{a.department} · <span className="font-mono">{a.slug}</span></p>
                  {!a.active && a.inactive_message && <p className="mt-1 text-[12px] text-red-700">{a.inactive_message}</p>}
                </div>
                <div className="flex gap-3 text-[11px] uppercase tracking-[0.14em]">
                  <Link to={`/agentes/${a.slug}`} className="inline-flex items-center gap-1 text-slate-700 hover:text-accent-blue" data-testid={`open-agent-${a.slug}`}>
                    <ExternalLink className="h-3 w-3" /> Abrir
                  </Link>
                  <Link to={`/agentes/${a.slug}/metricas`} className="inline-flex items-center gap-1 text-slate-700 hover:text-accent-blue" data-testid={`metrics-agent-${a.slug}`}>
                    <BarChart3 className="h-3 w-3" /> Métricas
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div data-testid="unassigned-agents">
        <p className="eyebrow">Agentes sin empresa ({unassigned.length})</p>
        <p className="mt-1 max-w-3xl text-[12px] text-slate-500">
          Agentes generados antes de que la empresa fuera obligatoria. Están inactivos (no responden) hasta asignarlos.
          La asignación es única y no se puede cambiar después. Si la sesión existe, se asigna la sesión completa.
        </p>
        {unassigned.length === 0 ? <p className="mt-3 text-sm text-slate-400" data-testid="unassigned-empty">No hay agentes sin empresa.</p> : (
          <ul className="mt-3 divide-y divide-slate-200 border border-slate-200">
            {unassigned.map((a) => (
              <li key={a.slug} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`unassigned-row-${a.slug}`}>
                <div className="min-w-0">
                  <p className="font-display text-base text-ink">{a.name || a.slug}</p>
                  <p className="text-[12px] text-slate-500">
                    {a.department} · creado {fmtDate(a.created_at)} · sesión <span className="font-mono">{a.session_id || "—"}</span>
                    {a.session_exists ? "" : " (ya no existe)"}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={target[a.slug] || company.slug} onValueChange={(v) => setTarget((t) => ({ ...t, [a.slug]: v }))}>
                    <SelectTrigger className="h-8 w-52 rounded-sm text-[12px]" data-testid={`unassigned-target-${a.slug}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={() => assign(a)} className="h-8 rounded-sm bg-slate-900 text-[11px] uppercase tracking-[0.14em] text-white"
                          data-testid={`assign-agent-${a.slug}`}>
                    Asignar
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
